import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useMotionValueEvent, animate } from "motion/react";
import {
  catalogueItemApi,
  cataloguePhotoApi,
  catalogueSectionApi,
  productApi,
  productCategoryApi,
  pageLayoutApi,
  publicShowroomApi,
  toAbsUrl,
} from "../api/api";
import "./public-catalogue.css";
import "./common.css";
import SearchableDropdown from "../components/SearchableDropdown";

/* ── Luxury Motion Carousel with Spring Physics & Touch Drag ── */
function PublicSectionCarousel({ items, renderCard }) {
  const containerRef = React.useRef(null);
  const trackRef = React.useRef(null);
  const [maxDrag, setMaxDrag] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const x = useMotionValue(0);

  const calculateBounds = React.useCallback(() => {
    if (!containerRef.current || !trackRef.current) return;
    const containerW = containerRef.current.offsetWidth;
    const trackW = trackRef.current.scrollWidth;
    const max = Math.max(0, trackW - containerW);
    setMaxDrag(max);

    const curr = x.get();
    setCanScrollLeft(curr < -10);
    setCanScrollRight(curr > -max + 10);
  }, [x]);

  useEffect(() => {
    calculateBounds();
    window.addEventListener("resize", calculateBounds);
    const t = setTimeout(calculateBounds, 350);
    return () => {
      window.removeEventListener("resize", calculateBounds);
      clearTimeout(t);
    };
  }, [calculateBounds, items]);

  useMotionValueEvent(x, "change", (latest) => {
    setCanScrollLeft(latest < -10);
    setCanScrollRight(latest > -maxDrag + 10);

    if (trackRef.current) {
      const card = trackRef.current.querySelector(".rv-pub-card");
      if (card) {
        const cardW = card.offsetWidth + 20;
        const idx = Math.min(items.length - 1, Math.max(0, Math.round(-latest / cardW)));
        setActiveSlide(idx);
      }
    }
  });

  const handleStep = (direction) => {
    if (!containerRef.current || !trackRef.current) return;
    const card = trackRef.current.querySelector(".rv-pub-card");
    const step = card ? (card.offsetWidth + 20) : 280;
    const curr = x.get();
    let target = direction === "left" ? curr + step : curr - step;
    target = Math.max(-maxDrag, Math.min(0, target));

    animate(x, target, {
      type: "spring",
      stiffness: 260,
      damping: 28,
      mass: 0.8,
    });
  };

  const jumpToSlide = (idx) => {
    if (!containerRef.current || !trackRef.current) return;
    const card = trackRef.current.querySelector(".rv-pub-card");
    const step = card ? (card.offsetWidth + 20) : 280;
    const target = Math.max(-maxDrag, Math.min(0, -idx * step));
    animate(x, target, {
      type: "spring",
      stiffness: 260,
      damping: 28,
    });
  };

  return (
    <div className="rv-section-carousel-wrapper" ref={containerRef}>
      <button
        type="button"
        className="rv-carousel-arrow rv-carousel-arrow--prev"
        onClick={() => handleStep("left")}
        disabled={!canScrollLeft}
        aria-label="Previous items"
        title="Previous items"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <div className="rv-carousel-viewport">
        <motion.div
          ref={trackRef}
          className="rv-section-carousel-track"
          drag="x"
          dragConstraints={{ left: -maxDrag, right: 0 }}
          dragElastic={0.14}
          dragTransition={{ bounceStiffness: 280, bounceDamping: 26, power: 0.22 }}
          style={{ x }}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => {
            setTimeout(() => setIsDragging(false), 90);
          }}
        >
          {items.map((item, idx) => renderCard(item, false, idx, isDragging))}
        </motion.div>
      </div>

      <button
        type="button"
        className="rv-carousel-arrow rv-carousel-arrow--next"
        onClick={() => handleStep("right")}
        disabled={!canScrollRight}
        aria-label="Next items"
        title="Next items"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {/* Luxury Minimalist Indicator Dots */}
      {items.length > 1 && (
        <div className="rv-carousel-indicator-bar">
          {items.slice(0, Math.min(items.length, 6)).map((_, i) => (
            <span
              key={i}
              className={`rv-carousel-dot ${i === activeSlide ? "rv-carousel-dot--active" : ""}`}
              onClick={() => jumpToSlide(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Luxury Motion Horizontal Panorama Strip with Drag Physics ── */
function PublicSectionHorizontal({ items, renderCard }) {
  const containerRef = React.useRef(null);
  const trackRef = React.useRef(null);
  const [maxDrag, setMaxDrag] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const x = useMotionValue(0);

  const calculateBounds = React.useCallback(() => {
    if (!containerRef.current || !trackRef.current) return;
    const containerW = containerRef.current.offsetWidth;
    const trackW = trackRef.current.scrollWidth;
    setMaxDrag(Math.max(0, trackW - containerW));
  }, []);

  useEffect(() => {
    calculateBounds();
    window.addEventListener("resize", calculateBounds);
    const t = setTimeout(calculateBounds, 350);
    return () => {
      window.removeEventListener("resize", calculateBounds);
      clearTimeout(t);
    };
  }, [calculateBounds, items]);

  return (
    <div className="rv-horizontal-viewport" ref={containerRef}>
      <motion.div
        ref={trackRef}
        className="rv-section-horizontal"
        drag="x"
        dragConstraints={{ left: -maxDrag, right: 0 }}
        dragElastic={0.16}
        dragTransition={{ bounceStiffness: 300, bounceDamping: 28, power: 0.28 }}
        style={{ x }}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => {
          setTimeout(() => setIsDragging(false), 90);
        }}
      >
        {items.map((item, idx) => renderCard(item, false, idx, isDragging))}
      </motion.div>
    </div>
  );
}

/* ── Toast notification ── */
function Toast({ message, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div
      className="rv-pub-toast"
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        background: "#110F0E",
        color: "#FDF8F0",
        padding: "12px 24px",
        borderRadius: 8,
        border: "1px solid rgba(197, 160, 89, 0.4)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
        zIndex: 2000,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 10,
        letterSpacing: "0.04em",
      }}
    >
      <span style={{ color: "#D4AF37", fontWeight: "bold" }}>✓</span>
      <span>{message}</span>
    </div>
  );
}

/* ── Full-Page Product Detail Showcase ── */
function ProductDetailShowcase({
  item,
  product,
  category,
  photos,
  onBack,
  onShare,
  relatedItems = [],
  onSelectItem,
  itemPhotosMap = {},
  productMap = new Map(),
  wishlist = [],
  onToggleWishlist,
}) {
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isCopiedCode, setIsCopiedCode] = useState(false);
  const [isCopiedLink, setIsCopiedLink] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const itemId = item.catalogueItemID ?? item.catalogueItemId ?? item.CatalogueItemID ?? item.id;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActivePhotoIdx(0);
  }, [itemId]);

  const getPhotoUrl = (ph) => {
    if (!ph) return null;
    const rawPath =
      ph.filepath ||
      ph.Filepath ||
      ph.filename ||
      ph.Filename ||
      (Array.isArray(ph.photos) ? ph.photos[0] : ph.photos) ||
      (Array.isArray(ph.Photos) ? ph.Photos[0] : ph.Photos);
    if (!rawPath || typeof rawPath !== "string") return null;
    return toAbsUrl(rawPath);
  };

  const safePhotos = Array.isArray(photos) ? photos : [];
  const activePhoto = safePhotos[activePhotoIdx] || safePhotos[0];
  const activeSrc = getPhotoUrl(activePhoto);
  const productCode = product?.productCode || "-";
  const isWishlisted = wishlist.includes(itemId);

  const handleCopyCode = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(productCode);
      setIsCopiedCode(true);
      setTimeout(() => setIsCopiedCode(false), 2000);
    }
  };

  const handleShareLink = async () => {
    const shareUrl = `${window.location.origin}/catalogue?item=${itemId}`;
    if (navigator?.share) {
      try {
        await navigator.share({
          title: `Reva Jewellers — ${item.displayName}`,
          text: `Explore ${item.displayName} (Ref: ${productCode}) at Reva Jewellers`,
          url: shareUrl,
        });
        return;
      } catch { }
    }
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl);
      setIsCopiedLink(true);
      setToastMessage("Product link copied to clipboard");
      setTimeout(() => setIsCopiedLink(false), 2500);
    } else {
      onShare(item, productCode);
    }
  };

  // Keyboard navigation for fullscreen lightbox
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsFullscreen(false);
        setIsZoomed(false);
      } else if (e.key === "ArrowLeft" && safePhotos.length > 1) {
        setActivePhotoIdx((prev) => (prev > 0 ? prev - 1 : safePhotos.length - 1));
        setIsZoomed(false);
      } else if (e.key === "ArrowRight" && safePhotos.length > 1) {
        setActivePhotoIdx((prev) => (prev < safePhotos.length - 1 ? prev + 1 : 0));
        setIsZoomed(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, safePhotos.length]);

  return (
    <div className="rv-pub-detail-page">
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} />}

      {/* ── Breadcrumb & Back Navigation Bar ── */}
      <div className="rv-pub-breadcrumb-bar">
        <button type="button" className="rv-pub-back-btn" onClick={onBack}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>Back to Collection</span>
        </button>

        <nav className="rv-pub-breadcrumb-trail" aria-label="Breadcrumb">
          <button type="button" onClick={onBack} className="rv-pub-breadcrumb-link">
            Showcase
          </button>
          <span className="rv-pub-breadcrumb-sep">/</span>
          <span className="rv-pub-breadcrumb-cat">{category?.categoryName || item.displaySection || "Jewellery"}</span>
          <span className="rv-pub-breadcrumb-sep rv-pub-breadcrumb-sep--item">/</span>
          <span className="rv-pub-breadcrumb-current" title={item.displayName}>{item.displayName}</span>
        </nav>
      </div>

      {/* ── Main 2-Column Product Showcase ── */}
      <div className="rv-pub-detail-grid">
        {/* Left: Product Image Gallery */}
        <div className="rv-pub-detail-gallery-card">
          <div
            className="rv-pub-detail-main-photo-box"
            onClick={() => activeSrc && setIsFullscreen(true)}
            title={activeSrc ? "Click to open fullscreen image" : ""}
          >
            {activeSrc ? (
              <>
                <img src={activeSrc} alt={item.displayName} className="rv-pub-detail-main-img" />
                <button
                  type="button"
                  className="rv-pub-zoom-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFullscreen(true);
                  }}
                  title="View full resolution"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="11" y1="8" x2="11" y2="14" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
              </>
            ) : (
              <div className="rv-pub-detail-no-img">
                <span style={{ fontSize: 32 }}>💎</span>
                <span>Fine Atelier Creation</span>
              </div>
            )}

            {/* Subtle Featured Badge (Commented out per UX review) */}
            {/* {item.isFeatured && (
              <div className="rv-pub-card-top-badges">
                <span className="rv-badge rv-badge--gold">⭐ FEATURED HIGHLIGHT</span>
              </div>
            )} */}
          </div>

          {/* Genuine Thumbnails Strip */}
          {safePhotos.length > 1 && (
            <div className="rv-pub-thumb-slider">
              {safePhotos.map((ph, idx) => {
                const src = getPhotoUrl(ph);
                return (
                  <button
                    key={ph.photoID || idx}
                    type="button"
                    className={`rv-pub-thumb-btn ${activePhotoIdx === idx ? "rv-pub-thumb-btn--active" : ""}`}
                    onClick={() => {
                      setActivePhotoIdx(idx);
                      setIsZoomed(false);
                    }}
                    title={`View angle ${idx + 1}`}
                  >
                    {src ? <img src={src} alt={`Thumbnail ${idx + 1}`} className="rv-pub-thumb-img" /> : <span>#{idx + 1}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Product Information Panel */}
        <div className="rv-pub-detail-info-card">
          {/* Top Metadata & Sharing Bar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: "#8F7B67", textTransform: "uppercase" }}>
                REF: {productCode}
              </span>
              <button
                type="button"
                onClick={handleCopyCode}
                title="Copy reference code"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: isCopiedCode ? "#2E7D32" : "#8F7B67",
                  fontSize: 11,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  padding: "2px 4px",
                }}
              >
                {isCopiedCode ? "✓ Copied" : "📋"}
              </button>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              {onToggleWishlist && (
                <button
                  type="button"
                  className="rv-pub-btn-outline"
                  style={{ padding: "5px 12px", fontSize: 11, color: isWishlisted ? "#D4AF37" : "inherit" }}
                  onClick={() => onToggleWishlist(itemId, item.displayName)}
                >
                  {isWishlisted ? "♥ Wishlisted" : "♡ Save"}
                </button>
              )}
              <button
                type="button"
                className="rv-pub-btn-outline"
                style={{ padding: "5px 12px", fontSize: 11 }}
                onClick={handleShareLink}
              >
                {isCopiedLink ? "✓ Copied" : "🔗 Share Link"}
              </button>
            </div>
          </div>

          {/* Product Name & Short Description */}
          <div>
            <h1 className="rv-pub-detail-title">{item.displayName}</h1>
            {item.descriptionLbl && <p className="rv-pub-detail-sublabel">{item.descriptionLbl}</p>}
          </div>

          {/* Pricing Box */}
          {/* <div className="rv-pub-detail-price-box">
            <span className="rv-pub-detail-price">
              {product?.sellingPrice || product?.price || item?.price
                ? `₹${Number(product?.sellingPrice || product?.price || item?.price).toLocaleString("en-IN")}`
                : product?.pricingType === "Fixed"
                  ? "Fixed Price"
                  : "Price on Request"}
            </span>
          </div> */}

          {/* Primary Product Details (Factual Table) */}
          <div className="rv-pub-detail-specs-section">
            <h4 className="rv-pub-detail-section-title">Product Details</h4>

            {item.description && (
              <div
                className="ql-editor"
                style={{
                  padding: 0,
                  minHeight: "auto",
                  fontSize: 13.5,
                  color: "#5C554F",
                  lineHeight: 1.65,
                  margin: "0 0 14px 0",
                }}
                dangerouslySetInnerHTML={{
                  __html: /<[a-z][\s\S]*>/i.test(item.description)
                    ? item.description
                    : item.description.replace(/\n/g, "<br/>"),
                }}
              />
            )}

            <div className="rv-pub-spec-table">
              {category?.categoryName && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">Category</span>
                  <span className="rv-pub-spec-row-val">{category.categoryName}</span>
                </div>
              )}
              {product?.purityStandard && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">Precious Metal Purity</span>
                  <span className="rv-pub-spec-row-val">{product.purityStandard}</span>
                </div>
              )}
              {item.displaySection && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">Collection</span>
                  <span className="rv-pub-spec-row-val">{item.displaySection}</span>
                </div>
              )}
              {product?.pricingType && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">Pricing Structure</span>
                  <span className="rv-pub-spec-row-val">{product.pricingType}</span>
                </div>
              )}
              {(product?.codeHSN || category?.codeHSN) && (
                <div className="rv-pub-spec-row">
                  <span className="rv-pub-spec-row-lbl">HSN Code</span>
                  <span className="rv-pub-spec-row-val">{product?.codeHSN || category?.codeHSN}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Related Items / Completer Pieces ── */}
      {relatedItems.length > 0 && (
        <section style={{ marginTop: 60 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h3 style={{ fontFamily: "var(--rv-font-serif)", fontSize: 24, fontWeight: 700, margin: 0, textTransform: "uppercase" }}>
                You May Also Like
              </h3>
              <p style={{ fontSize: 12.5, color: "#8F7B67", margin: "4px 0 0 0" }}>
                From the same collection — crafted in matching precious alloys
              </p>
            </div>
            <button type="button" className="rv-pub-btn-outline" onClick={onBack}>
              Explore Full Collection →
            </button>
          </div>

          <div className="rv-pub-grid">
            {relatedItems.slice(0, 4).map((rel) => {
              const relPId = Number(rel.productID ?? rel.productId ?? rel.ProductID ?? rel.id);
              const relItemId = rel.catalogueItemID ?? rel.catalogueItemId ?? rel.CatalogueItemID ?? rel.id;
              const relProd = productMap.get(relPId);
              const relPhotos = itemPhotosMap[relItemId] || [];
              const sorted = [...relPhotos].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
              const primaryObj = sorted.find((p) => p.isPrimary || p.IsPrimary) || sorted[0];
              const secondaryObj = sorted.find((p) => p !== primaryObj) || null;

              const getRelSrc = (p) => {
                if (!p) return null;
                return p.filepath?.includes("/") ? toAbsUrl(p.filepath) : toAbsUrl(p.photos?.[0]);
              };

              const relPrimarySrc = getRelSrc(primaryObj);
              const relSecondarySrc = getRelSrc(secondaryObj);
              const relCode = relProd?.productCode || "-";

              return (
                <div
                  key={relItemId}
                  className="rv-pub-card"
                  onClick={() => onSelectItem(rel)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="rv-pub-card-media">
                    {relPrimarySrc ? (
                      <>
                        <img src={relPrimarySrc} alt={rel.displayName} className="rv-pub-card-img" />
                        {relSecondarySrc && (
                          <img
                            src={relSecondarySrc}
                            alt={`${rel.displayName} alternate view`}
                            className="rv-pub-card-img rv-pub-card-img--secondary"
                          />
                        )}
                      </>
                    ) : (
                      <div className="rv-pub-card-no-img">
                        <span style={{ fontSize: 26 }}>💎</span>
                        <span>Fine Jewellery</span>
                      </div>
                    )}

                    {/* Featured badge (Commented out per UX review) */}
                    {/* {rel.isFeatured && (
                      <div className="rv-pub-card-top-badges">
                        <span className="rv-badge rv-badge--gold">⭐ FEATURED</span>
                      </div>
                    )} */}
                  </div>

                  <div className="rv-pub-card-body">
                    <div className="rv-pub-card-meta-row">
                      <span>{relCode}</span>
                    </div>
                    <h4 className="rv-pub-card-title">{rel.displayName}</h4>
                    <button type="button" className="rv-pub-btn-solid" style={{ marginTop: "auto", width: "100%" }}>
                      EXPLORE DESIGN →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── High-Definition Fullscreen Lightbox Overlay ── */}
      {isFullscreen && activeSrc && (
        <div
          className="rv-pub-lightbox-overlay"
          onClick={() => {
            setIsFullscreen(false);
            setIsZoomed(false);
          }}
        >
          {/* Top Bar */}
          <div className="rv-pub-lightbox-top-bar" onClick={(e) => e.stopPropagation()}>
            <div style={{ color: "#FFFFFF" }}>
              <div style={{ fontFamily: "var(--rv-font-serif)", fontSize: 20, fontWeight: 700 }}>
                {item.displayName}
              </div>
              <div style={{ fontSize: 11, color: "#C5A059", letterSpacing: "0.1em" }}>
                REFERENCE: {productCode} {safePhotos.length > 1 ? `· Angle ${activePhotoIdx + 1} of ${safePhotos.length}` : ""}
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <button
                type="button"
                className="rv-pub-btn-outline"
                style={{ color: "#FFFFFF", borderColor: "rgba(255,255,255,0.4)", background: "rgba(0,0,0,0.4)" }}
                onClick={() => setIsZoomed((prev) => !prev)}
              >
                {isZoomed ? "Reset Zoom" : "Zoom In"}
              </button>
              <button
                type="button"
                className="rv-pub-btn-solid"
                style={{ background: "#FFFFFF", color: "#110F0E", borderColor: "#FFFFFF" }}
                onClick={() => {
                  setIsFullscreen(false);
                  setIsZoomed(false);
                }}
              >
                ✕ Close
              </button>
            </div>
          </div>

          {/* Lightbox Image Stage */}
          <div
            style={{
              maxWidth: "85vw",
              maxHeight: "80vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activeSrc}
              alt={item.displayName}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                transform: isZoomed ? "scale(1.8)" : "scale(1)",
                transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                cursor: isZoomed ? "zoom-out" : "zoom-in",
              }}
              onClick={() => setIsZoomed((prev) => !prev)}
            />
          </div>

          {/* Navigation Arrows for Multiple Photos */}
          {safePhotos.length > 1 && (
            <>
              <button
                type="button"
                className="rv-pub-lightbox-nav-btn rv-pub-lightbox-nav-btn--prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setActivePhotoIdx((prev) => (prev > 0 ? prev - 1 : safePhotos.length - 1));
                  setIsZoomed(false);
                }}
                title="Previous image"
              >
                ‹
              </button>
              <button
                type="button"
                className="rv-pub-lightbox-nav-btn rv-pub-lightbox-nav-btn--next"
                onClick={(e) => {
                  e.stopPropagation();
                  setActivePhotoIdx((prev) => (prev < safePhotos.length - 1 ? prev + 1 : 0));
                  setIsZoomed(false);
                }}
                title="Next image"
              >
                ›
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN PUBLIC SHOWCASE COMPONENT
════════════════════════════════════════════ */
export default function PublicCatalogue() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [itemPhotosMap, setItemPhotosMap] = useState({});
  const [cmsSections, setCmsSections] = useState([]); // CMS-managed homepage sections
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [selectedSection, setSelectedSection] = useState("all");
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedAlloy, setSelectedAlloy] = useState("all");
  const [sortBy, setSortBy] = useState("curated");
  const [selectedModalItem, setSelectedModalItem] = useState(null);
  const [toast, setToast] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // LocalStorage-backed wishlist
  const [wishlist, setWishlist] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("reva_wishlist") || "[]");
    } catch {
      return [];
    }
  });

  const toggleWishlist = (itemId, displayName) => {
    if (!itemId) return;
    const normId = Number(itemId);
    setWishlist((prev) => {
      const exists = prev.some((id) => Number(id) === normId);
      const next = exists ? prev.filter((id) => Number(id) !== normId) : [...prev, normId];
      try {
        localStorage.setItem("reva_wishlist", JSON.stringify(next));
      } catch { }
      setToast(exists ? `Removed "${displayName}" from Wishlist` : `Saved "${displayName}" to Wishlist ♥`);
      return next;
    });
  };

  const [searchParams, setSearchParams] = useSearchParams();

  /* ── Fetch all public data ── */
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [itemsData, prodData, catData, allPhotosData, cmsData, allSectionsData, layoutIdsData] = await Promise.all([
          catalogueItemApi.getAll().catch(() => []),
          productApi.getAll().catch(() => []),
          productCategoryApi.getAll().catch(() => []),
          cataloguePhotoApi.getAll().catch(() => []),
          publicShowroomApi.getHomepage().catch(() => []),
          catalogueSectionApi.getAll("", { skipAuthRedirect: true }).catch(() => []),
          pageLayoutApi.getDraft("homepage").catch(() => []),
        ]);

        const allSectionsList = Array.isArray(allSectionsData) ? allSectionsData : allSectionsData?.data ?? [];
        const unpublishedSectionNames = new Set(
          allSectionsList
            .filter((s) => s && (!s.isPublished || s.isArchived))
            .map((s) => (s.publicTitle || s.title || s.internalName || "").trim().toLowerCase())
            .filter(Boolean)
        );

        const rawItems = Array.isArray(itemsData) ? itemsData : itemsData?.data ?? [];
        const activeItems = rawItems.filter((i) => {
          if (i.isActive === false) return false;
          const secName = (i.displaySection || "").trim().toLowerCase();
          if (secName && unpublishedSectionNames.has(secName)) return false;
          return true;
        });
        setItems(activeItems);

        const prodList = Array.isArray(prodData) ? prodData : prodData?.data ?? [];
        setProducts(prodList);

        const catList = Array.isArray(catData) ? catData : catData?.data ?? [];
        setCategories(catList);

        // Store CMS sections in configured homepage order
        const cmsList = Array.isArray(cmsData) ? cmsData : [];
        const layoutIds = Array.isArray(layoutIdsData) ? layoutIdsData : [];
        const cmsMap = new Map(cmsList.map((s) => [s.sectionID, s]));
        
        const publishedSections = allSectionsList
          .filter((s) => s.isPublished && !s.isArchived)
          .map((s) => ({ ...(cmsMap.get(s.sectionID) || {}), ...s }));

        publishedSections.sort((a, b) => {
          if (layoutIds.length > 0) {
            const idxA = layoutIds.indexOf(a.sectionID);
            const idxB = layoutIds.indexOf(b.sectionID);
            if (idxA !== -1 && idxB !== -1) return idxA - idxB;
            if (idxA !== -1) return -1;
            if (idxB !== -1) return 1;
          }
          if (cmsList.length > 0) {
            const cmsIdxA = cmsList.findIndex((s) => s.sectionID === a.sectionID);
            const cmsIdxB = cmsList.findIndex((s) => s.sectionID === b.sectionID);
            if (cmsIdxA !== -1 && cmsIdxB !== -1) return cmsIdxA - cmsIdxB;
            if (cmsIdxA !== -1) return -1;
            if (cmsIdxB !== -1) return 1;
          }
          return (a.displayOrder || 0) - (b.displayOrder || 0);
        });

        setCmsSections(publishedSections.length > 0 ? publishedSections : cmsList);

        // Group photos by catalogue item ID
        const photosMap = {};
        const rawPhotos = Array.isArray(allPhotosData) ? allPhotosData : allPhotosData?.data ?? [];
        for (const ph of rawPhotos) {
          const itId = ph.catalogueItemID ?? ph.catalogueItemId ?? ph.CatalogueItemID;
          if (itId != null) {
            if (!photosMap[itId]) photosMap[itId] = [];
            photosMap[itId].push(ph);
          }
        }

        // Also fetch getByItemId for any active items to ensure complete photo isolation
        await Promise.all(
          activeItems.map(async (it) => {
            const itId = it.catalogueItemID ?? it.catalogueItemId ?? it.CatalogueItemID ?? it.id;
            if (itId != null && (!photosMap[itId] || photosMap[itId].length === 0)) {
              try {
                const phData = await cataloguePhotoApi.getByItemId(itId);
                const list = Array.isArray(phData) ? phData : phData?.data ?? [];
                if (list.length > 0) {
                  photosMap[itId] = list;
                }
              } catch {
                if (!photosMap[itId]) photosMap[itId] = [];
              }
            }
          })
        );
        setItemPhotosMap(photosMap);
      } catch (err) {
        console.error("Public catalogue error:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const { id: urlParamId } = useParams();

  /* ── Check direct link from URL query params (e.g. ?item=5 or ?code=RJ-001 or /catalogue/5) ── */
  useEffect(() => {
    const itemParam = urlParamId || searchParams.get("item");
    const codeParam = searchParams.get("code");
    if (items.length > 0) {
      if (itemParam) {
        const found = items.find((i) => String(i.catalogueItemID ?? i.id) === String(itemParam));
        if (found) setSelectedModalItem(found);
      } else if (codeParam) {
        const prod = products.find((p) => (p.productCode || "").toLowerCase() === codeParam.toLowerCase());
        if (prod) {
          const found = items.find((i) => i.productID === prod.productID);
          if (found) setSelectedModalItem(found);
        }
      }
    }
  }, [items, products, searchParams, urlParamId]);

  /* ── Fast lookup maps ── */
  const productMap = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      const id = Number(p.productID ?? p.productId ?? p.ProductID ?? p.id);
      if (!isNaN(id)) map.set(id, p);
    }
    return map;
  }, [products]);

  const categoryMap = useMemo(() => {
    const map = new Map();
    for (const c of categories) {
      const id = Number(c.categoryID ?? c.categoryId ?? c.CategoryID ?? c.id);
      if (!isNaN(id)) map.set(id, c);
    }
    return map;
  }, [categories]);

  // Dynamic Featured Hero Items (for Top Spotlight Card)
  const [heroSlideIdx, setHeroSlideIdx] = useState(0);
  const [heroPhotoIdx, setHeroPhotoIdx] = useState(0);

  const featuredHeroItems = useMemo(() => {
    return items.filter((it) => {
      const isItemFeatured = Boolean(it.isFeatured || it.IsFeatured);
      const isHero = (it.displaySection || it.DisplaySection || "").toLowerCase().includes("hero");
      if (!isItemFeatured && !isHero) return false;
      const itemId = it.catalogueItemID ?? it.catalogueItemId ?? it.CatalogueItemID ?? it.id;
      const photos = itemPhotosMap[itemId] || itemPhotosMap[String(itemId)] || [];
      return photos.length > 0;
    });
  }, [items, itemPhotosMap]);

  const activeHeroItem =
    featuredHeroItems.length > 0
      ? featuredHeroItems[heroSlideIdx % featuredHeroItems.length]
      : null;

  // Auto-rotate featured hero items every 3 seconds
  useEffect(() => {
    if (featuredHeroItems.length <= 1) return;
    const interval = setInterval(() => {
      setHeroSlideIdx((prev) => (prev + 1) % featuredHeroItems.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [featuredHeroItems.length]);

  useEffect(() => {
    setHeroPhotoIdx(0);
  }, [heroSlideIdx]);

  /* ── Dynamic Filter Sections ── */
  const sections = useMemo(() => {
    const raw = items
      .map((i) => i.displaySection || i.DisplaySection)
      .filter(Boolean);
    return Array.from(new Set(raw));
  }, [items]);

  /* ── Curated Metal & Precious Material Options ── */
  const metalOptions = [
    { value: "all", label: "METALS: ALL MATERIALS" },
    { value: "gold", label: "✨ GOLD (18K & 22K)" },
    { value: "diamond", label: "💎 DIAMOND & SOLITAIRES" },
    { value: "silver", label: "🥈 SILVER 925" },
    { value: "platinum", label: "⚪ PLATINUM" },
  ];

  /* ── Dynamic Collection Options for Multi-Select Dropdown ── */
  const collectionOptions = useMemo(() => {
    return [
      { section: "all", label: "COLLECTIONS: ALL CREATIONS" },
      ...sections.map((sec) => ({
        section: sec,
        label: sec.toUpperCase(),
      })),
    ];
  }, [sections]);

  /* ── Sort Options for SearchableDropdown ── */
  const sortOptions = [
    // { value: "curated", label: "SORT: ATELIER CURATED" },
    { value: "featured-first", label: "SORT: FEATURED" },
    { value: "name-asc", label: "SORT: DESIGN NAME (A-Z)" },
  ];

  /* ── Filtered & Sorted Showcase Items ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);

    let res = items.filter((item) => {
      const pId = Number(item.productID ?? item.productId ?? item.ProductID ?? item.id);
      const itemId = item.catalogueItemID ?? item.catalogueItemId ?? item.CatalogueItemID ?? item.id;
      const prod = productMap.get(pId);
      const prodName = prod?.productName || prod?.ProductName || prod?.name || "";
      const prodCode = prod?.productCode || prod?.ProductCode || prod?.itemCode || prod?.code || "";
      const catId = Number(prod?.categoryID ?? prod?.categoryId ?? prod?.CategoryID);
      const cat = categoryMap.get(catId);
      const catName = cat?.categoryName || cat?.CategoryName || cat?.name || "";

      const displayName = item.displayName || item.DisplayName || item.name || "";
      const descLbl = item.descriptionLbl || item.DescriptionLbl || "";
      const desc = item.description || item.Description || "";
      const section = item.displaySection || item.DisplaySection || "";
      const purity = prod?.purityStandard || prod?.PurityStandard || "";
      const pricing = prod?.pricingType || prod?.PricingType || "";

      // Wishlist check
      if (selectedSection === "wishlist") {
        if (!wishlist.includes(itemId)) return false;
      }

      // Hero Section check (featured)
      const isItemFeatured = Boolean(item.isFeatured || item.IsFeatured);
      if (selectedSection === "featured" && !isItemFeatured) {
        return false;
      }

      // Collections & CMS Section check
      if (selectedCollections && selectedCollections.length > 0) {
        const matchesCol = selectedCollections.some((col) => {
          const colClean = col.trim().toLowerCase();
          // 1. Direct or partial match on item.displaySection
          if (section) {
            const secClean = section.trim().toLowerCase();
            if (secClean === colClean) return true;
            if (secClean.includes(colClean) || colClean.includes(secClean)) return true;
          }
          // 2. Check if item is mapped in this CMS section
          const cmsSec = cmsSections.find((s) => {
            const sTitle = (s.publicTitle || "").toLowerCase();
            const sName  = (s.internalName || "").toLowerCase();
            const sSlug  = (s.slug || "").toLowerCase();
            return sTitle === colClean || sName === colClean || sSlug === colClean ||
                   sTitle.includes(colClean) || colClean.includes(sTitle);
          });
          if (cmsSec && Array.isArray(cmsSec.products)) {
            if (cmsSec.products.some((p) => Number(p.catalogueItemID) === Number(itemId))) {
              return true;
            }
          }
          return false;
        });
        if (!matchesCol) return false;
      }

      // Precious Metal / Material check (Gold, Diamond, Silver, Platinum)
      if (selectedAlloy !== "all") {
        const target = selectedAlloy.toLowerCase();
        const prodPurity = (purity || "").toLowerCase();
        const combined = `${displayName} ${descLbl} ${catName} ${desc} ${prodPurity}`.toLowerCase();

        if (target === "gold") {
          const isGold = combined.includes("gold") || combined.includes("18k") || combined.includes("22k") || combined.includes("24k") || combined.includes("14k") || prodPurity.includes("k");
          if (!isGold) return false;
        } else if (target === "diamond") {
          const isDiamond = combined.includes("diamond") || combined.includes("solitaire") || combined.includes("carat") || combined.includes("emerald-cut");
          if (!isDiamond) return false;
        } else if (target === "silver") {
          const isSilver = combined.includes("silver") || combined.includes("925") || catName.toLowerCase().includes("silver");
          if (!isSilver) return false;
        } else if (target === "platinum") {
          const isPlatinum = combined.includes("platinum") || combined.includes("plat");
          if (!isPlatinum) return false;
        } else {
          if (!combined.includes(target)) return false;
        }
      }

      // Search token matching
      if (tokens.length === 0) return true;

      const haystack = [
        displayName,
        descLbl,
        desc,
        section,
        prodName,
        prodCode,
        catName,
        purity,
        pricing,
      ]
        .join(" ")
        .toLowerCase();

      return tokens.every((token) => haystack.includes(token));
    });

    // Sorting
    if (sortBy === "name-asc") {
      res.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
    } else if (sortBy === "featured-first") {
      res.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
    }

    return res;
  }, [items, search, selectedSection, selectedCollections, selectedCategory, selectedAlloy, sortBy, wishlist, productMap, categoryMap, cmsSections]);

  /* ── Share Item Link ── */
  const handleShare = (item, code) => {
    const itemId = item.catalogueItemID ?? item.catalogueItemId ?? item.CatalogueItemID ?? item.id;
    const shareUrl = `${window.location.origin}/catalogue?item=${itemId}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl);
      setToast(`Registry link copied to clipboard!`);
    } else {
      setToast(`Item Code: ${code}`);
    }
  };

  /* ── Back to Full Catalogue Grid & Reset Section ── */
  const handleBackToGrid = () => {
    setSelectedModalItem(null);
    setSearchParams({});
    setSelectedSection("all");
    setSelectedCollections([]);
    setSelectedAlloy("all");
    setSelectedCategory("all");
    setSearch("");
  };

  /* ── Unified Navbar Section Selector ── */
  const handleNavSelect = (sec) => {
    setSelectedModalItem(null);
    setSearchParams({});
    setSelectedSection(sec);
    if (sec === "all") {
      setSelectedCollections([]);
      setSelectedAlloy("all");
      setSelectedCategory("all");
      setSearch("");
    }
    setMobileNavOpen(false);
  };

  /* ── Helper to resolve primary & secondary photos ── */
  const getItemPhotos = (itemId) => {
    const photos =
      itemPhotosMap[itemId] ||
      itemPhotosMap[String(itemId)] ||
      itemPhotosMap[Number(itemId)] ||
      [];
    if (!photos.length) return { primary: null, secondary: null, count: 0 };
    const sorted = [...photos].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    const primaryObj = sorted.find((p) => p.isPrimary || p.IsPrimary) || sorted[0];
    const secondaryObj = sorted.find((p) => p !== primaryObj) || null;

    const getSrc = (p) => {
      if (!p) return null;
      const rawPath =
        p.filepath ||
        p.Filepath ||
        p.filename ||
        p.Filename ||
        (Array.isArray(p.photos) ? p.photos[0] : p.photos) ||
        (Array.isArray(p.Photos) ? p.Photos[0] : p.Photos);
      return rawPath && typeof rawPath === "string" ? toAbsUrl(rawPath) : null;
    };

    return {
      primary: getSrc(primaryObj),
      secondary: getSrc(secondaryObj),
      count: photos.length,
    };
  };

  return (
    <div className="rv-pub-cat">
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      {/* ── Main Atelier Navigation Header ── */}
      <header className="rv-pub-header">
        <div className="rv-pub-header-inner">
          <div className="rv-pub-brand" onClick={handleBackToGrid} style={{ cursor: "pointer" }}>
            <div className="rv-pub-logo-mark">
              <span className="rv-pub-logo-dot" />
              <span className="rv-pub-logo-dot--sm" />
            </div>
            <div className="rv-pub-brand-info">
              <span className="rv-pub-brand-text">REVA JEWELLERS</span>
              <span className="rv-pub-brand-subtext">HAUTE JOAILLERIE & SOLITAIRE ATELIER</span>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="rv-pub-nav-links rv-pub-nav-links--desktop">
            <button
              type="button"
              className={`rv-pub-nav-item ${selectedSection === "all" && selectedCollections.length === 0 ? "rv-pub-nav-item--active" : ""}`}
              onClick={() => handleNavSelect("all")}
            >
              ALL JEWELLERY
            </button>
            <button
              type="button"
              className={`rv-pub-nav-item ${selectedSection === "featured" ? "rv-pub-nav-item--active" : ""}`}
              onClick={() => handleNavSelect("featured")}
            >
              FEATURED
            </button>
            <button
              type="button"
              className={`rv-pub-nav-item ${selectedCollections.length > 0 ? "rv-pub-nav-item--active" : ""}`}
              onClick={() => {
                handleNavSelect("all");
                document.getElementById("showcase")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              COLLECTIONS
            </button>
            {wishlist.length > 0 && (
              <button
                type="button"
                className={`rv-pub-nav-item ${selectedSection === "wishlist" ? "rv-pub-nav-item--active" : ""}`}
                style={{ color: "#D4AF37", fontWeight: 700 }}
                onClick={() => handleNavSelect("wishlist")}
              >
                ♥ WISHLIST ({wishlist.length})
              </button>
            )}
          </nav>

          {/* Mobile Hamburger Toggle */}
          <button
            type="button"
            className="rv-pub-hamburger"
            onClick={() => setMobileNavOpen((prev) => !prev)}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileNavOpen}
          >
            <span className={`rv-pub-hamburger-line ${mobileNavOpen ? "rv-pub-hamburger-line--open-1" : ""}`} />
            <span className={`rv-pub-hamburger-line ${mobileNavOpen ? "rv-pub-hamburger-line--open-2" : ""}`} />
            <span className={`rv-pub-hamburger-line ${mobileNavOpen ? "rv-pub-hamburger-line--open-3" : ""}`} />
          </button>
        </div>

        {/* Mobile Slide-Down Nav Drawer */}
        {mobileNavOpen && (
          <div className="rv-pub-mobile-nav">
            <button
              type="button"
              className={`rv-pub-mobile-nav-item ${selectedSection === "all" && selectedCollections.length === 0 ? "rv-pub-mobile-nav-item--active" : ""}`}
              onClick={() => handleNavSelect("all")}
            >
              ALL JEWELLERY
            </button>
            <button
              type="button"
              className={`rv-pub-mobile-nav-item ${selectedSection === "featured" ? "rv-pub-mobile-nav-item--active" : ""}`}
              onClick={() => handleNavSelect("featured")}
            >
              FEATURED
            </button>
            <button
              type="button"
              className={`rv-pub-mobile-nav-item ${selectedCollections.length > 0 ? "rv-pub-mobile-nav-item--active" : ""}`}
              onClick={() => {
                handleNavSelect("all");
                setMobileNavOpen(false);
                document.getElementById("showcase")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              COLLECTIONS
            </button>
            {wishlist.length > 0 && (
              <button
                type="button"
                className={`rv-pub-mobile-nav-item ${selectedSection === "wishlist" ? "rv-pub-mobile-nav-item--active" : ""}`}
                style={{ color: "#D4AF37", fontWeight: 700 }}
                onClick={() => handleNavSelect("wishlist")}
              >
                ♥ WISHLIST ({wishlist.length})
              </button>
            )}
          </div>
        )}
      </header>

      {/* ── Conditional Content: Single Product Showcase vs Full Catalogue Grid ── */}
      {selectedModalItem ? (
        <ProductDetailShowcase
          item={selectedModalItem}
          product={productMap.get(Number(selectedModalItem.productID ?? selectedModalItem.productId))}
          category={categoryMap.get(
            Number(
              productMap.get(Number(selectedModalItem.productID ?? selectedModalItem.productId))?.categoryID ??
              productMap.get(Number(selectedModalItem.productID ?? selectedModalItem.productId))?.categoryId
            )
          )}
          photos={
            itemPhotosMap[selectedModalItem.catalogueItemID ?? selectedModalItem.catalogueItemId ?? selectedModalItem.CatalogueItemID ?? selectedModalItem.id] ||
            itemPhotosMap[String(selectedModalItem.catalogueItemID ?? selectedModalItem.id)] ||
            []
          }
          relatedItems={items.filter(
            (it) =>
              (it.catalogueItemID ?? it.id) !== (selectedModalItem.catalogueItemID ?? selectedModalItem.id) &&
              (it.displaySection === selectedModalItem.displaySection || it.productID === selectedModalItem.productID)
          )}
          onBack={handleBackToGrid}
          onShare={handleShare}
          onSelectItem={(newItem) => {
            setSelectedModalItem(newItem);
            const nId = newItem.catalogueItemID ?? newItem.id;
            setSearchParams({ item: nId });
          }}
          itemPhotosMap={itemPhotosMap}
          productMap={productMap}
          wishlist={wishlist}
          onToggleWishlist={toggleWishlist}
        />
      ) : (
        <>
          {/* ── Editorial Hero Banner (Split Spotlight when featured items exist, centered otherwise) ── */}
          <section className="rv-pub-hero">
            {activeHeroItem ? (
              <div className="rv-pub-hero-split">
                {/* Left Column: Heading, Subheading, Search & Chips */}
                <div className="rv-pub-hero-split-left">
                  <div className="rv-pub-hero-intro">
                    <span className="rv-pub-hero-badge">
                      ✨ EXQUISITE HAUTE JOAILLERIE SHOWCASE
                    </span>
                    <h1 className="rv-pub-hero-title">
                      Timeless Luxury, <em>Handcrafted</em> for Generations
                    </h1>
                    <p className="rv-pub-hero-desc">
                      Explore hallmarked silver & gold creations, certified fine jewelry, and bespoke atelier pieces curated directly from our salon.
                    </p>
                  </div>

                  <div className="rv-pub-hero-actions">
                    {/* Luxury Search Group */}
                    <div className="rv-pub-search-group">
                      <svg className="rv-pub-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        type="text"
                        className="rv-pub-search-input"
                        placeholder="Search by hallmark, carat, cut, ID..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      {search && (
                        <button
                          type="button"
                          onClick={() => setSearch("")}
                          style={{ background: "none", border: "none", color: "#8F7B67", cursor: "pointer", marginRight: 8, fontSize: 13 }}
                        >
                          ✕
                        </button>
                      )}
                      <button
                        type="button"
                        className="rv-pub-search-btn"
                        onClick={() => document.getElementById("showcase")?.scrollIntoView({ behavior: "smooth" })}
                        aria-label="Discover collection"
                        title="Discover collection"
                      >
                        <span className="rv-pub-search-btn-text">DISCOVER</span>
                        <span className="rv-pub-search-btn-arrow">→</span>
                      </button>
                    </div>

                    {/* Quick Curation Filter Pills (Fast shortcuts: ALL CREATIONS, FEATURED, and COLLECTIONS Dropdown) */}
                    <div className="rv-pub-hero-chips">
                      <button
                        type="button"
                        className={`rv-pub-chip ${selectedSection === "all" && selectedCollections.length === 0 && selectedAlloy === "all" && !search ? "rv-pub-chip--active" : ""}`}
                        onClick={() => {
                          setSelectedSection("all");
                          setSelectedCollections([]);
                          setSelectedAlloy("all");
                          setSearch("");
                        }}
                      >
                        ALL CREATIONS ({items.length})
                      </button>
                      <button
                        type="button"
                        className={`rv-pub-chip rv-pub-chip--featured ${selectedSection === "featured" ? "rv-pub-chip--active" : ""}`}
                        onClick={() => {
                          setSelectedSection("featured");
                          setSelectedCollections([]);
                        }}
                      >
                        ⭐ FEATURED
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Column: Featured Hero Spotlight Card (Stitch Design) */}
                <div className="rv-pub-hero-split-right">
                  {(() => {
                    const heroItemId = activeHeroItem.catalogueItemID ?? activeHeroItem.catalogueItemId ?? activeHeroItem.CatalogueItemID ?? activeHeroItem.id;
                    const heroPhotos = itemPhotosMap[heroItemId] || itemPhotosMap[String(heroItemId)] || [];
                    const sorted = [...heroPhotos].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
                    const currentHeroPhoto = sorted[heroPhotoIdx % (sorted.length || 1)] || sorted[0];
                    const getHeroSrc = (p) => {
                      if (!p) return null;
                      const rawPath =
                        p.filepath ||
                        p.Filepath ||
                        p.filename ||
                        p.Filename ||
                        (Array.isArray(p.photos) ? p.photos[0] : p.photos) ||
                        (Array.isArray(p.Photos) ? p.Photos[0] : p.Photos);
                      return rawPath && typeof rawPath === "string" ? toAbsUrl(rawPath) : null;
                    };
                    const heroSrc = getHeroSrc(currentHeroPhoto);

                    return (
                      <div
                        className="rv-pub-hero-card"
                        onClick={() => {
                          setSelectedModalItem(activeHeroItem);
                          setSearchParams({ item: heroItemId });
                        }}
                      >
                        <AnimatePresence initial={false}>
                          {heroSrc ? (
                            <motion.img
                              key={heroItemId + "_" + (currentHeroPhoto?.photoID || heroPhotoIdx)}
                              src={heroSrc}
                              alt={activeHeroItem.displayName}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 1.0, ease: "easeInOut" }}
                              className="rv-pub-hero-card-img"
                            />
                          ) : (
                            <motion.div
                              key="no-img"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.8 }}
                              style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#B9A792" }}
                            >
                              <span>💎 Fine Haute Joaillerie</span>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <div className="rv-pub-hero-card-overlay">
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={heroItemId}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -6 }}
                              transition={{ duration: 0.35, ease: "easeOut" }}
                            >
                              <h3 className="rv-pub-hero-card-title">{activeHeroItem.displayName}</h3>
                              {activeHeroItem.descriptionLbl && (
                                <p className="rv-pub-hero-card-desc">
                                  {activeHeroItem.descriptionLbl}
                                </p>
                              )}
                            </motion.div>
                          </AnimatePresence>

                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                            <span style={{ fontSize: 11, letterSpacing: "0.14em", fontWeight: 700, color: "#D4AF37", textTransform: "uppercase" }}>
                              EXPLORE MASTERPIECE →
                            </span>

                            {featuredHeroItems.length > 1 && (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="rv-pub-hero-nav-dot-btn"
                                  onClick={() => setHeroSlideIdx((prev) => (prev > 0 ? prev - 1 : featuredHeroItems.length - 1))}
                                  title="Previous featured piece"
                                >
                                  ‹
                                </button>
                                <span style={{ fontSize: 10, color: "#FFFFFF", fontWeight: 700, letterSpacing: "0.08em" }}>
                                  {(heroSlideIdx % featuredHeroItems.length) + 1} / {featuredHeroItems.length}
                                </span>
                                <button
                                  type="button"
                                  className="rv-pub-hero-nav-dot-btn"
                                  onClick={() => setHeroSlideIdx((prev) => (prev < featuredHeroItems.length - 1 ? prev + 1 : 0))}
                                  title="Next featured piece"
                                >
                                  ›
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div className="rv-pub-hero-inner">
                <span className="rv-pub-hero-badge">
                  ✨ EXQUISITE HAUTE JOAILLERIE SHOWCASE
                </span>
                <h1 className="rv-pub-hero-title">
                  Timeless Luxury, <em>Handcrafted</em> for Generations
                </h1>
                <p className="rv-pub-hero-desc">
                  Explore hallmarked silver & gold creations, certified fine jewelry, and bespoke atelier pieces curated directly from our salon.
                </p>

                {/* Luxury Search Group */}
                <div className="rv-pub-search-group">
                  <svg className="rv-pub-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    className="rv-pub-search-input"
                    placeholder="Search by hallmark, carat, cut, ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      style={{ background: "none", border: "none", color: "#8F7B67", cursor: "pointer", marginRight: 8, fontSize: 13 }}
                    >
                      ✕
                    </button>
                  )}
                  <button
                    type="button"
                    className="rv-pub-search-btn"
                    onClick={() => document.getElementById("showcase")?.scrollIntoView({ behavior: "smooth" })}
                    aria-label="Discover collection"
                    title="Discover collection"
                  >
                    <span className="rv-pub-search-btn-text">DISCOVER</span>
                    <span className="rv-pub-search-btn-arrow">→</span>
                  </button>
                </div>

                {/* Quick Curation Filter Pills (Fast shortcuts: ALL CREATIONS, FEATURED, and COLLECTIONS Dropdown) */}
                <div className="rv-pub-hero-chips">
                  <button
                    type="button"
                    className={`rv-pub-chip ${selectedSection === "all" && selectedCollections.length === 0 && selectedAlloy === "all" && !search ? "rv-pub-chip--active" : ""}`}
                    onClick={() => {
                      setSelectedSection("all");
                      setSelectedCollections([]);
                      setSelectedAlloy("all");
                      setSearch("");
                    }}
                  >
                    ALL CREATIONS ({items.length})
                  </button>
                  <button
                    type="button"
                    className={`rv-pub-chip rv-pub-chip--featured ${selectedSection === "featured" ? "rv-pub-chip--active" : ""}`}
                    onClick={() => {
                      setSelectedSection("featured");
                      setSelectedCollections([]);
                    }}
                  >
                    ⭐ FEATURED
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* ── Dynamic Admin-Managed CMS Sections (Homepage Showcase) ── */}
          {selectedSection === "all" && !search && selectedCollections.length === 0 && selectedAlloy === "all" && cmsSections.length > 0 && (
            <div className="rv-pub-cms-sections" style={{ maxWidth: 1400, margin: "0 auto", padding: "0 28px", width: "100%" }}>
              {cmsSections
                .filter((sec) => sec.isPublished && sec.sectionType !== "HERO_BANNER")
                .map((sec) => {
                  const mappings = Array.isArray(sec.products) ? sec.products : [];
                  // Resolve items mapped to this section
                  const sectionItems = mappings
                    .map((m) => {
                      const found = items.find(
                        (it) => Number(it.catalogueItemID ?? it.id) === Number(m.catalogueItemID)
                      );
                      return found ? { ...found, badgeText: m.badgeText } : null;
                    })
                    .filter(Boolean);

                  // If no products mapped yet, fallback to matching by displaySection name, or active catalogue items
                  const matchingSectionItems = items.filter((it) => {
                    const secLower = (it.displaySection || "").toLowerCase();
                    const sTitle = (sec.publicTitle || "").toLowerCase();
                    const sName  = (sec.internalName || "").toLowerCase();
                    return secLower && (sTitle.includes(secLower) || sName.includes(secLower));
                  });

                  const fallbackItems = sectionItems.length > 0
                    ? sectionItems
                    : matchingSectionItems.length > 0
                    ? matchingSectionItems.slice(0, 12)
                    : items.slice(0, 8);

                  if (fallbackItems.length === 0) return null;

                  const layoutType = (sec.layoutType || (sec.sectionType === "CAROUSEL" ? "carousel" : "grid")).toLowerCase();

                  const renderCard = (item, isGrid = false, idx = 0, isDragging = false) => {
                    const pId = Number(item.productID ?? item.productId ?? item.id);
                    const itemId = item.catalogueItemID ?? item.catalogueItemId ?? item.id;
                    const prod = productMap.get(pId);
                    const { primary, secondary } = getItemPhotos(itemId);
                    const purity = prod?.purityStandard || "-";
                    const isWishlisted = wishlist.includes(itemId);

                    const cardMediaAndBody = (
                      <>
                        <div className="rv-pub-card-media">
                          {primary ? (
                            <>
                              <img src={primary} alt={item.displayName} className="rv-pub-card-img" />
                              {secondary && (
                                <img src={secondary} alt={`${item.displayName} view`} className="rv-pub-card-img rv-pub-card-img--secondary" />
                              )}
                            </>
                          ) : (
                            <div className="rv-pub-card-no-img">
                              <span style={{ fontSize: 26 }}>💎</span>
                              <span>High Jewellery Piece</span>
                            </div>
                          )}

                          <div className="rv-pub-card-top-badges">
                            {item.badgeText ? (
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: "3px 8px",
                                borderRadius: 14, background: "#C5A059", color: "#FFFFFF",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                              }}>
                                {item.badgeText}
                              </span>
                            ) : purity ? (
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: "3px 8px",
                                borderRadius: 14, background: "rgba(17,15,14,0.75)",
                                color: "#D4AF37", backdropFilter: "blur(4px)",
                              }}>
                                {purity}
                              </span>
                            ) : null}

                            <button
                              type="button"
                              className={`rv-pub-card-bookmark ${isWishlisted ? "rv-pub-card-bookmark--active" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                toggleWishlist(itemId, item.displayName);
                              }}
                              aria-label={isWishlisted ? "Remove from Wishlist" : "Save to Wishlist"}
                              title={isWishlisted ? "Remove from Wishlist" : "Save to Wishlist"}
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill={isWishlisted ? "currentColor" : "none"}
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ pointerEvents: "none" }}
                              >
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className="rv-pub-card-body" style={{ padding: "16px 18px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#8F7B67", textTransform: "uppercase" }}>
                              {item.displaySection || "FINE CREATION"}
                            </span>
                            {prod?.productCode && (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontFamily: "monospace", color: "#8F7B67", background: "rgba(185,167,146,0.14)", padding: "1px 6px", borderRadius: 4 }}>
                                {prod.productCode}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard?.writeText(prod.productCode);
                                    setToast(`Product code copied: ${prod.productCode}`);
                                  }}
                                  title="Copy product code"
                                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, color: "#8F7B67", display: "inline-flex", alignItems: "center" }}
                                >
                                  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                </button>
                              </span>
                            )}
                          </div>
                          <h3 className="rv-pub-card-title" style={{ fontSize: 15, margin: "4px 0 6px" }}>
                            {item.displayName}
                          </h3>
                          {item.descriptionLbl && (
                            <p className="rv-pub-card-desc" style={{ fontSize: 12, color: "#736B66", margin: 0 }}>
                              {item.descriptionLbl}
                            </p>
                          )}
                          <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#C5A059", letterSpacing: "0.08em" }}>
                              EXPLORE PIECE →
                            </span>
                          </div>
                        </div>
                      </>
                    );

                    const handleCardClick = () => {
                      if (isDragging) return;
                      setSelectedModalItem(item);
                      setSearchParams({ item: itemId });
                    };

                    if (isGrid) {
                      return (
                        <motion.div
                          key={itemId}
                          className="rv-pub-card"
                          variants={{
                            hidden: { opacity: 0, y: 30, scale: 0.95 },
                            visible: {
                              opacity: 1,
                              y: 0,
                              scale: 1,
                              transition: {
                                duration: 0.5,
                                ease: [0.16, 1, 0.3, 1],
                              },
                            },
                          }}
                          whileHover={{
                            y: -8,
                            boxShadow: "0 18px 40px rgba(17, 15, 14, 0.12), 0 0 0 1px rgba(197, 160, 89, 0.38)",
                            transition: { duration: 0.25 },
                          }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleCardClick}
                        >
                          {cardMediaAndBody}
                        </motion.div>
                      );
                    }

                    return (
                      <motion.div
                        key={itemId}
                        className="rv-pub-card"
                        whileHover={{
                          y: -6,
                          boxShadow: "0 16px 36px rgba(17, 15, 14, 0.12), 0 0 0 1px rgba(197, 160, 89, 0.35)",
                          transition: { duration: 0.22 },
                        }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleCardClick}
                      >
                        {cardMediaAndBody}
                      </motion.div>
                    );
                  };

                  return (
                    <section
                      key={sec.sectionID}
                      style={{
                        padding: "44px 0 32px",
                        borderBottom: "1px solid rgba(185, 167, 146, 0.22)",
                      }}
                    >
                      {/* Section Header */}
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        style={{
                          display: "flex", justifyContent: "space-between", alignItems: "flex-end",
                          marginBottom: 24, flexWrap: "wrap", gap: 14,
                        }}
                      >
                        <div>
                          <div style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
                            color: "#C5A059", textTransform: "uppercase", marginBottom: 4,
                          }}>
                            <span>✦</span>
                            <span>{layoutType === "carousel" ? "Atelier Gallery Carousel" : layoutType === "horizontal" ? "Curated Panorama" : "Curated Heritage Edit"}</span>
                          </div>
                          <h2 style={{
                            fontFamily: "var(--rv-font-serif)", fontSize: 26, fontWeight: 700,
                            color: "#110F0E", margin: 0,
                          }}>
                            {sec.publicTitle}
                          </h2>
                          {sec.subtitle && (
                            <p style={{ fontSize: 13.5, color: "#736B66", margin: "6px 0 0", maxWidth: 640 }}>
                              {sec.subtitle}
                            </p>
                          )}
                        </div>

                        <motion.button
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.96 }}
                          type="button"
                          className="rv-pub-btn-outline"
                          onClick={() => {
                            setSelectedSection("all");
                            setSelectedCollections([sec.publicTitle || sec.internalName]);
                            const el = document.getElementById("showcase");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                          }}
                          style={{
                            fontSize: 12, padding: "8px 18px", borderRadius: 24,
                            borderColor: "rgba(197, 160, 89, 0.5)", color: "#110F0E",
                            background: "#FFFFFF",
                          }}
                        >
                          View All in Collection →
                        </motion.button>
                      </motion.div>

                      {/* Section Products: Carousel vs Horizontal vs Grid */}
                      {layoutType === "carousel" ? (
                        <PublicSectionCarousel items={fallbackItems} renderCard={renderCard} />
                      ) : layoutType === "horizontal" ? (
                        <PublicSectionHorizontal items={fallbackItems} renderCard={renderCard} />
                      ) : (
                        <motion.div
                          className="rv-section-grid"
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true, amount: 0.08 }}
                          variants={{
                            hidden: { opacity: 0 },
                            visible: {
                              opacity: 1,
                              transition: {
                                staggerChildren: 0.07,
                                delayChildren: 0.05,
                              },
                            },
                          }}
                        >
                          {fallbackItems.map((item, idx) => renderCard(item, true, idx))}
                        </motion.div>
                      )}
                    </section>
                  );
                })}
            </div>
          )}

          {/* ── Curated Showcase Controls Bar ── */}
          <div className="rv-pub-controls" id="showcase">
            <div className="rv-pub-controls-inner">
              <div className="rv-pub-controls-left">
                <span className="rv-pub-controls-title">Curated Showcase</span>
                <span className="rv-pub-controls-count">
                  SHOWING {filtered.length} OF {items.length} MASTER CREATIONS
                </span>
              </div>

              <div className="rv-pub-controls-right">
                {/* Collection / Section Multi-Select Filter — Searchable Dropdown */}
                {sections.length > 0 && (
                  <SearchableDropdown
                    theme="public"
                    isMulti={true}
                    options={collectionOptions}
                    valueKey="section"
                    labelKey="label"
                    value={selectedCollections}
                    onChange={(val) => {
                      setSelectedCollections(val);
                      setSelectedSection("all");
                    }}
                    placeholder="COLLECTIONS: ALL"
                    searchPlaceholder="Search collections…"
                    pageSize={10}
                    allowClear={true}
                    className="rv-pub-sd-collection"
                  />
                )}

                {/* Metal & Precious Material Filter — Searchable Dropdown */}
                <SearchableDropdown
                  theme="public"
                  options={metalOptions}
                  valueKey="value"
                  labelKey="label"
                  value={selectedAlloy}
                  onChange={(val) => setSelectedAlloy(val)}
                  placeholder="METALS: ALL MATERIALS"
                  searchPlaceholder="Search metals / gems…"
                  pageSize={10}
                  allowClear={false}
                  className="rv-pub-sd-metal"
                />

                {/* Sort Dropdown — Searchable Dropdown */}
                <SearchableDropdown
                  theme="public"
                  options={sortOptions}
                  valueKey="value"
                  labelKey="label"
                  value={sortBy}
                  onChange={(val) => setSortBy(val)}
                  placeholder="SORT: FEATURED"
                  searchPlaceholder="Search sorting…"
                  pageSize={10}
                  allowClear={false}
                  className="rv-pub-sd-sort"
                />

                {/* Reset Filters Shortcut when active */}
                {(selectedAlloy !== "all" || selectedSection !== "all" || selectedCollections.length > 0 || search || sortBy !== "curated") && (
                  <button
                    type="button"
                    className="rv-pub-btn-outline"
                    style={{ padding: "7px 12px", fontSize: 11 }}
                    onClick={() => {
                      setSearch("");
                      setSelectedSection("all");
                      setSelectedCollections([]);
                      setSelectedAlloy("all");
                      setSortBy("curated");
                    }}
                  >
                    ✕ Reset
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Product Showcase Grid with Motion Animations ── */}
          <main className="rv-pub-main">
            {loading ? (
              <div style={{ textAlign: "center", padding: "100px 0", color: "#8F7B67" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>💎</div>
                <p style={{ fontFamily: "var(--rv-font-serif)", fontSize: 20, fontWeight: 700, color: "#110F0E" }}>
                  Curating Reva High Jewellery Collection…
                </p>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 20px", color: "#6E655F" }}>
                <p style={{ fontFamily: "var(--rv-font-serif)", fontSize: 24, fontWeight: 700, marginBottom: 8, color: "#110F0E" }}>
                  No Masterpieces Found
                </p>
                <p style={{ fontSize: 13, color: "#8F7B67", marginBottom: 20 }}>
                  {selectedSection === "wishlist"
                    ? "You haven't saved any jewellery creations to your wishlist yet."
                    : selectedAlloy === "Silver"
                      ? "No silver jewellery pieces found with the current filter settings."
                      : search
                        ? `No matching creations for "${search}". Try searching with a different term or registry code.`
                        : "No designs currently listed matching the selected category and metal criteria."}
                </p>
                <button
                  type="button"
                  className="rv-pub-btn-gold"
                  onClick={() => {
                    setSearch("");
                    setSelectedSection("all");
                    setSelectedCategory("all");
                    setSelectedAlloy("all");
                  }}
                >
                  Show All Creations
                </button>
              </div>
            ) : (
              <div className="rv-pub-grid">
                <AnimatePresence mode="popLayout">
                  {filtered.map((item, idx) => {
                    const pId = Number(item.productID ?? item.productId ?? item.ProductID ?? item.id);
                    const itemId = item.catalogueItemID ?? item.catalogueItemId ?? item.CatalogueItemID ?? item.id;
                    const prod = productMap.get(pId);
                    const { primary, secondary } = getItemPhotos(itemId);
                    const productCode = prod?.productCode || prod?.ProductCode || prod?.itemCode || "-";
                    const purity = prod?.purityStandard || "-";
                    const categoryName = categoryMap.get(Number(prod?.categoryID ?? prod?.categoryId))?.categoryName || "-";
                    const isWishlisted = wishlist.includes(itemId);

                    return (
                      <motion.div
                        key={itemId}
                        layout
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.3), ease: [0.16, 1, 0.3, 1] }}
                        className="rv-pub-card"
                        onClick={() => {
                          setSelectedModalItem(item);
                          setSearchParams({ item: itemId });
                        }}
                      >
                        {/* Media Frame with Luxury Aspect Ratio & 2-Angle Hover */}
                        <div className="rv-pub-card-media">
                          {primary ? (
                            <>
                              <img src={primary} alt={item.displayName} className="rv-pub-card-img" />
                              {secondary && (
                                <img src={secondary} alt={`${item.displayName} alternate view`} className="rv-pub-card-img rv-pub-card-img--secondary" />
                              )}
                            </>
                          ) : (
                            <div className="rv-pub-card-no-img">
                              <span style={{ fontSize: 30 }}>💎</span>
                              <span>Fine Atelier Creation</span>
                            </div>
                          )}

                          {/* Simplified Single Badge + Wishlist */}
                          <div className="rv-pub-card-top-badges">
                            {/* Top-left badges (Commented out per UX review)
                            <div className="rv-pub-badge-stack">
                              {item.isFeatured ? (
                                <span className="rv-badge rv-badge--gold">⭐ FEATURED</span>
                              ) : (
                                <span className="rv-badge rv-badge--cert">BIS HALLMARKED</span>
                              )}
                            </div>
                            */}

                            <button
                              type="button"
                              className={`rv-pub-card-bookmark ${isWishlisted ? "rv-pub-card-bookmark--active" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                toggleWishlist(itemId, item.displayName);
                              }}
                              aria-label={isWishlisted ? "Remove from Wishlist" : "Save to Wishlist"}
                              title={isWishlisted ? "Remove from Wishlist" : "Save to Wishlist"}
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill={isWishlisted ? "currentColor" : "none"}
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ pointerEvents: "none" }}
                              >
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {/* Card Body */}
                        <div className="rv-pub-card-body">
                          {/* Subtle Secondary Metadata */}
                          <div className="rv-pub-card-meta-row">
                            <span className="rv-pub-card-code" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                              <span>{productCode}</span>
                              {productCode !== "-" && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard?.writeText(productCode);
                                    setToast(`Product code copied: ${productCode}`);
                                  }}
                                  title="Copy reference code"
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    padding: 0,
                                    color: "inherit",
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                </button>
                              )}
                            </span>
                            <span className="rv-pub-card-cat">{categoryName}</span>
                          </div>

                          {/* Roomy Product Title */}
                          <h3 className="rv-pub-card-title">
                            {item.displayName}
                          </h3>

                          {/* Clean 2-Specification Row (No Awkward Wrapping) */}
                          <div className="rv-pub-spec-pill">
                            <div className="rv-pub-spec-col">
                              <span className="rv-pub-spec-lbl">PRECIOUS METAL</span>
                              <span className="rv-pub-spec-val">{purity}</span>
                            </div>
                            <div className="rv-pub-spec-col">
                              <span className="rv-pub-spec-lbl">
                                {prod?.grossWeight ? "GROSS WEIGHT" : "COLLECTION"}
                              </span>
                              <span className="rv-pub-spec-val">
                                {prod?.grossWeight ? `${prod.grossWeight}g` : (item.displaySection || "Showroom Signature")}
                              </span>
                            </div>
                          </div>

                          {/* Clear Pricing Area */}
                          {/* <div className="rv-pub-card-price-row">
                            <div className="rv-pub-card-price-box">
                              <span className="rv-pub-card-price">
                                {prod?.sellingPrice || prod?.price || item?.price
                                  ? `₹${Number(prod?.sellingPrice || prod?.price || item?.price).toLocaleString("en-IN")}`
                                  : prod?.pricingType === "Fixed"
                                    ? "Fixed Price"
                                    : "Price on Request"}
                              </span>
                            </div>
                          </div> */}

                          {/* Informative Primary Action */}
                          <div className="rv-pub-card-actions">
                            <button
                              type="button"
                              className="rv-pub-btn-solid"
                              style={{ width: "100%" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedModalItem(item);
                                setSearchParams({ item: itemId });
                              }}
                            >
                              EXPLORE DESIGN →
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </main>
        </>
      )}
    </div>
  );
}
